export type AnimatableProperty =
  | 'opacity'
  | 'positionX'
  | 'positionY'
  | 'scaleX'
  | 'scaleY'
  | 'rotation'
  | 'anchorX'
  | 'anchorY'
  | 'exposure'
  | 'contrast'
  | 'saturation'
  | 'hue'
  | 'temperature'
  | 'tint'
  | 'shadows'
  | 'midtones'
  | 'highlights'
  | 'whites'
  | 'blacks'
  | 'intensity'
  | 'blur'
  | 'sharpen'
  | 'vignette'
  | 'glow'
  | 'noise';

export type KeyframeInterpolation = 'linear' | 'easeIn' | 'easeOut' | 'easeInOut' | 'hold';

/** Unit-square bezier handle (CSS cubic-bezier style). */
export interface BezierHandle {
  x: number;
  y: number;
}

export interface Keyframe {
  id: string;
  time: number; // seconds relative to clip start
  property: AnimatableProperty;
  value: number;
  interpolation: KeyframeInterpolation;
  /** Outgoing control toward the next key (unit square of that segment). */
  bezierOut?: BezierHandle;
  /** Incoming control from the previous key (unit square of that segment). */
  bezierIn?: BezierHandle;
}

export function bezierDefaults(mode: KeyframeInterpolation): {
  out: BezierHandle;
  in: BezierHandle;
} {
  switch (mode) {
    case 'easeIn':
      return { out: { x: 0.42, y: 0 }, in: { x: 1, y: 1 } };
    case 'easeOut':
      return { out: { x: 0, y: 0 }, in: { x: 0.58, y: 1 } };
    case 'easeInOut':
      return { out: { x: 0.42, y: 0 }, in: { x: 0.58, y: 1 } };
    case 'hold':
      return { out: { x: 0, y: 0 }, in: { x: 1, y: 0 } };
    default:
      // Linear
      return { out: { x: 1 / 3, y: 1 / 3 }, in: { x: 2 / 3, y: 2 / 3 } };
  }
}

export function createKeyframe(
  property: AnimatableProperty,
  time: number,
  value: number,
  interpolation: KeyframeInterpolation = 'linear'
): Keyframe {
  const bez = bezierDefaults(interpolation);
  return {
    id: crypto.randomUUID(),
    property,
    time,
    value,
    interpolation,
    bezierOut: { ...bez.out },
    bezierIn: { ...bez.in },
  };
}

/** Apply an ease preset — updates interpolation + both bezier handles. */
export function applyKeyframeEase(
  kf: Keyframe,
  mode: KeyframeInterpolation
): Keyframe {
  const bez = bezierDefaults(mode);
  return {
    ...kf,
    interpolation: mode,
    bezierOut: { ...bez.out },
    bezierIn: { ...bez.in },
  };
}

export function resolveSegmentBezier(
  a: Keyframe,
  b: Keyframe
): { x1: number; y1: number; x2: number; y2: number } {
  const aDef = bezierDefaults(a.interpolation);
  const bDef = bezierDefaults(b.interpolation);
  const out = a.bezierOut ?? aDef.out;
  const inn = b.bezierIn ?? bDef.in;
  return {
    x1: clamp(out.x, 0.001, 1),
    y1: out.y,
    x2: clamp(inn.x, 0, 0.999),
    y2: inn.y,
  };
}

/** Map unit-square handle → time/value on segment a→b. */
export function handleToWorld(
  a: Keyframe,
  b: Keyframe,
  handle: BezierHandle,
  valueFallbackSpan = 1
): { time: number; value: number } {
  const dt = b.time - a.time;
  const dv = b.value - a.value;
  const span = Math.abs(dv) > 1e-8 ? dv : valueFallbackSpan;
  return {
    time: a.time + handle.x * dt,
    value: a.value + handle.y * span,
  };
}

/** Map time/value → unit-square handle on segment a→b. */
export function worldToHandle(
  a: Keyframe,
  b: Keyframe,
  time: number,
  value: number,
  valueFallbackSpan = 1
): BezierHandle {
  const dt = b.time - a.time || 1e-6;
  const dv = b.value - a.value;
  const span = Math.abs(dv) > 1e-8 ? dv : valueFallbackSpan;
  return {
    x: clamp((time - a.time) / dt, 0.001, 0.999),
    y: (value - a.value) / span,
  };
}

/** Evaluate a property at time t from a sorted keyframe list for that property. */
export function evaluateKeyframes(
  keyframes: Keyframe[],
  property: AnimatableProperty,
  time: number
): number | null {
  const kfs = keyframes
    .filter((k) => k.property === property)
    .sort((a, b) => a.time - b.time);
  if (kfs.length === 0) return null;
  if (time <= kfs[0]!.time) return kfs[0]!.value;
  if (time >= kfs[kfs.length - 1]!.time) return kfs[kfs.length - 1]!.value;

  for (let i = 0; i < kfs.length - 1; i++) {
    const a = kfs[i]!;
    const b = kfs[i + 1]!;
    if (time >= a.time && time <= b.time) {
      if (a.interpolation === 'hold') return a.value;
      const t = (time - a.time) / (b.time - a.time || 1);
      const { x1, y1, x2, y2 } = resolveSegmentBezier(a, b);
      const eased = cubicBezierProgress(t, x1, y1, x2, y2);
      return a.value + (b.value - a.value) * eased;
    }
  }
  return kfs[kfs.length - 1]!.value;
}

function clamp(n: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, n));
}

function bezier1D(t: number, p0: number, p1: number, p2: number, p3: number) {
  const u = 1 - t;
  return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
}

function bezier1DDerivative(t: number, p0: number, p1: number, p2: number, p3: number) {
  const u = 1 - t;
  return (
    3 * u * u * (p1 - p0) + 6 * u * t * (p2 - p1) + 3 * t * t * (p3 - p2)
  );
}

/**
 * CSS cubic-bezier: given linear time progress t∈[0,1], return eased progress.
 * Solves Bx(u)=t then returns By(u).
 */
export function cubicBezierProgress(
  t: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;

  // Newton-Raphson for u where Bx(u) = t (P0=0, P3=1)
  let u = t;
  for (let i = 0; i < 8; i++) {
    const x = bezier1D(u, 0, x1, x2, 1);
    const dx = bezier1DDerivative(u, 0, x1, x2, 1);
    if (Math.abs(dx) < 1e-6) break;
    u -= (x - t) / dx;
    u = clamp(u, 0, 1);
  }
  return bezier1D(u, 0, y1, y2, 1);
}

/** dBy/dBx at normalized time t (for speed = Δv/Δt × this). */
export function cubicBezierSpeedFactor(
  t: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  if (t <= 0) {
    const dx = Math.max(1e-6, 3 * x1);
    return (3 * y1) / dx;
  }
  if (t >= 1) {
    const dx = Math.max(1e-6, 3 * (1 - x2));
    return (3 * (1 - y2)) / dx;
  }
  let u = t;
  for (let i = 0; i < 8; i++) {
    const x = bezier1D(u, 0, x1, x2, 1);
    const dx = bezier1DDerivative(u, 0, x1, x2, 1);
    if (Math.abs(dx) < 1e-6) break;
    u -= (x - t) / dx;
    u = clamp(u, 0, 1);
  }
  const dx = bezier1DDerivative(u, 0, x1, x2, 1);
  const dy = bezier1DDerivative(u, 0, y1, y2, 1);
  if (Math.abs(dx) < 1e-8) return 0;
  return dy / dx;
}

/** Instantaneous signed speed (units/sec) on segment a→b at absolute time. */
export function segmentSpeedAt(a: Keyframe, b: Keyframe, time: number): number {
  if (a.interpolation === 'hold') return 0;
  const D = b.time - a.time;
  if (D <= 1e-8) return 0;
  const dV = b.value - a.value;
  const t = clamp((time - a.time) / D, 0, 1);
  const { x1, y1, x2, y2 } = resolveSegmentBezier(a, b);
  return (dV / D) * cubicBezierSpeedFactor(t, x1, y1, x2, y2);
}

/** Absolute speed for the graph (|units|/sec). */
export function segmentAbsSpeedAt(a: Keyframe, b: Keyframe, time: number): number {
  return Math.abs(segmentSpeedAt(a, b, time));
}

export function keyOutgoingAbsSpeed(a: Keyframe, b: Keyframe): number {
  return segmentAbsSpeedAt(a, b, a.time);
}

export function keyIncomingAbsSpeed(a: Keyframe, b: Keyframe): number {
  return segmentAbsSpeedAt(a, b, b.time);
}

/**
 * Evaluate absolute speed at time for a property (0 outside segments / hold).
 */
export function evaluateKeyframeSpeed(
  keyframes: Keyframe[],
  property: AnimatableProperty,
  time: number
): number {
  const kfs = keyframes
    .filter((k) => k.property === property)
    .sort((a, b) => a.time - b.time);
  if (kfs.length < 2) return 0;
  for (let i = 0; i < kfs.length - 1; i++) {
    const a = kfs[i]!;
    const b = kfs[i + 1]!;
    if (time >= a.time && time <= b.time) {
      return segmentAbsSpeedAt(a, b, time);
    }
  }
  return 0;
}

/** Set outgoing influence (0.02–0.98) keeping current outgoing speed. */
export function setOutgoingInfluence(a: Keyframe, b: Keyframe, influence: number): Keyframe {
  const x1 = clamp(influence, 0.02, 0.98);
  const D = b.time - a.time || 1e-6;
  const dV = b.value - a.value;
  const speed = segmentSpeedAt(a, b, a.time); // signed
  // speed = (dV/D) * (y1/x1)  => y1 = speed * D / dV * x1
  let y1: number;
  if (Math.abs(dV) < 1e-8) {
    y1 = 0;
  } else {
    y1 = ((speed * D) / dV) * x1;
  }
  const cur = resolveSegmentBezier(a, b);
  return { ...a, bezierOut: { x: x1, y: y1 }, bezierIn: a.bezierIn ?? { x: cur.x2, y: cur.y2 } };
}

/** Set incoming influence on B for segment a→b, keeping incoming speed. */
export function setIncomingInfluence(a: Keyframe, b: Keyframe, influence: number): Keyframe {
  // influence from the end: how far left the handle extends, maps to (1 - x2)
  const inv = clamp(influence, 0.02, 0.98);
  const x2 = 1 - inv;
  const D = b.time - a.time || 1e-6;
  const dV = b.value - a.value;
  const speed = segmentSpeedAt(a, b, b.time);
  // speed = (dV/D) * (1-y2)/(1-x2) => 1-y2 = speed*D/dV*(1-x2) => y2 = 1 - ...
  let y2: number;
  if (Math.abs(dV) < 1e-8) {
    y2 = 1;
  } else {
    y2 = 1 - ((speed * D) / dV) * (1 - x2);
  }
  return { ...b, bezierIn: { x: x2, y: y2 } };
}

/** Set outgoing absolute speed at A, keeping influence x1. */
export function setOutgoingAbsSpeed(a: Keyframe, b: Keyframe, absSpeed: number): Keyframe {
  const cur = resolveSegmentBezier(a, b);
  const x1 = clamp(cur.x1, 0.02, 0.98);
  const D = b.time - a.time || 1e-6;
  const dV = b.value - a.value;
  const sign = dV < 0 ? -1 : 1;
  const speed = Math.max(0, absSpeed) * sign;
  let y1: number;
  if (Math.abs(dV) < 1e-8) {
    y1 = 0;
  } else {
    y1 = ((speed * D) / dV) * x1;
  }
  return { ...a, bezierOut: { x: x1, y: y1 } };
}

/** Set incoming absolute speed at B for segment a→b. */
export function setIncomingAbsSpeed(a: Keyframe, b: Keyframe, absSpeed: number): Keyframe {
  const cur = resolveSegmentBezier(a, b);
  const x2 = clamp(cur.x2, 0.02, 0.98);
  const D = b.time - a.time || 1e-6;
  const dV = b.value - a.value;
  const sign = dV < 0 ? -1 : 1;
  const speed = Math.max(0, absSpeed) * sign;
  let y2: number;
  if (Math.abs(dV) < 1e-8) {
    y2 = 1;
  } else {
    y2 = 1 - ((speed * D) / dV) * (1 - x2);
  }
  return { ...b, bezierIn: { x: x2, y: y2 } };
}

/**
 * Freely place the outgoing handle from graph (influence + speed together).
 * `influence` is 0–1 along the segment; `absSpeed` is |units|/sec at the key.
 */
export function setOutgoingHandle(
  a: Keyframe,
  b: Keyframe,
  influence: number,
  absSpeed: number
): Keyframe {
  const x1 = clamp(influence, 0.02, 0.98);
  const D = b.time - a.time || 1e-6;
  const dV = b.value - a.value;
  const sign = Math.abs(dV) < 1e-8 ? 1 : dV < 0 ? -1 : 1;
  const speed = Math.max(0, absSpeed) * sign;
  let y1: number;
  if (Math.abs(dV) < 1e-8) {
    // Flat value segment — encode speed pull via free y for creativity
    y1 = clamp(absSpeed / 100, -3, 3);
  } else {
    y1 = ((speed * D) / dV) * x1;
    // Allow overshoot past linear for creative easing
    y1 = clamp(y1, -3, 3);
  }
  return { ...a, bezierOut: { x: x1, y: y1 } };
}

/** Freely place the incoming handle on B for segment a→b. */
export function setIncomingHandle(
  a: Keyframe,
  b: Keyframe,
  influenceFromEnd: number,
  absSpeed: number
): Keyframe {
  const inv = clamp(influenceFromEnd, 0.02, 0.98);
  const x2 = 1 - inv;
  const D = b.time - a.time || 1e-6;
  const dV = b.value - a.value;
  const sign = Math.abs(dV) < 1e-8 ? 1 : dV < 0 ? -1 : 1;
  const speed = Math.max(0, absSpeed) * sign;
  let y2: number;
  if (Math.abs(dV) < 1e-8) {
    y2 = 1 - clamp(absSpeed / 100, -3, 3);
  } else {
    y2 = 1 - ((speed * D) / dV) * (1 - x2);
    y2 = clamp(y2, -2, 4);
  }
  return { ...b, bezierIn: { x: x2, y: y2 } };
}
